import React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material';
import SubNavigationView from './sub-navigation-view';
import { ViewProps } from './types';

/** Height of the compact bar (px). */
const COMPACT_BAR_HEIGHT_PX = 77;

/**
 * Compact single-row app bar for mobile: back button and destination text on one line.
 * Uses flexbox so the title sits directly next to the back button (left-aligned).
 * Used only in the trips app so we don't change the shared SecondaryAppBar in z4-lib.
 */
const CompactMobileAppBar = ({
  startAdornment,
  profile,
}: Pick<ViewProps, 'startAdornment' | 'profile'>) => {
  const primaryText = profile?.primary;
  if (primaryText == null) {
    return null;
  }

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        minHeight: COMPACT_BAR_HEIGHT_PX,
        height: COMPACT_BAR_HEIGHT_PX,
        zIndex: 1110,
      }}
    >
      <Toolbar
        variant="light"
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: COMPACT_BAR_HEIGHT_PX,
          height: COMPACT_BAR_HEIGHT_PX,
          maxHeight: COMPACT_BAR_HEIGHT_PX,
          maxWidth: 'none',
          width: '100%',
          gap: 0,
        }}
      >
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {startAdornment}
        </Box>
        <Typography
          variant="h4"
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {primaryText}
        </Typography>
      </Toolbar>
    </AppBar>
  );
};

/**
 * Renders compact single-row bar on mobile (back + destination on one line),
 * and full SubNavigation (SecondaryAppBar) on desktop.
 * Used only in the trips app for pages that want less mobile chrome.
 */
const ResponsiveSubNavigation = ({
  profile,
  startAdornment,
  endAdornment,
  tabs,
}: ViewProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('desktop'));

  if (isMobile && startAdornment != null && profile?.primary != null) {
    return (
      <CompactMobileAppBar
        startAdornment={startAdornment}
        profile={profile}
      />
    );
  }

  return (
    <SubNavigationView
      profile={profile}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      tabs={tabs}
    />
  );
};

export default ResponsiveSubNavigation;
